// Create: src/contexts/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'es' | 'fr' | 'de' | 'zh' | 'ar' | 'hi' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation dictionaries
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.cases': 'Cases',
    'nav.clients': 'Clients',
    'nav.documents': 'Documents',
    'nav.billing': 'Billing',
    'nav.team': 'Team',
    'nav.analytics': 'Analytics',
    
    // Common
    'common.welcome': 'Welcome',
    'common.loading': 'Loading',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    
    // Cases
    'cases.title': 'Case Pipeline',
    'cases.create_new': 'Create New Case',
    'cases.status.lead': 'Lead',
    'cases.status.collecting_docs': 'Collecting Documents',
    'cases.status.docs_ready': 'Documents Ready',
    'cases.status.submitted': 'Submitted',
    'cases.status.decision_pending': 'Decision Pending',
    'cases.status.complete': 'Complete',
    
    // Clients
    'clients.title': 'Client Management',
    'clients.add_new': 'Add New Client',
    'clients.total_clients': 'Total Clients',
    'clients.with_cases': 'With Cases',
    
    // Documents
    'documents.title': 'Document Intelligence',
    'documents.upload': 'Upload Documents',
    'documents.required': 'Required Documents',
    
    // Billing
    'billing.title': 'Billing Intelligence',
    'billing.invoices': 'Invoices',
    'billing.payments': 'Payments',
    
    // Team
    'team.title': 'Team Management',
    'team.members': 'Team Members',
    'team.performance': 'Performance',
    
    // Analytics
    'analytics.title': 'Analytics Dashboard',
    'analytics.overview': 'Overview',
    'analytics.performance': 'Performance'
  },
  es: {
    // Navigation
    'nav.dashboard': 'Panel de Control',
    'nav.cases': 'Casos',
    'nav.clients': 'Clientes',
    'nav.documents': 'Documentos',
    'nav.billing': 'Facturación',
    'nav.team': 'Equipo',
    'nav.analytics': 'Analíticas',
    
    // Common
    'common.welcome': 'Bienvenido',
    'common.loading': 'Cargando',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.view': 'Ver',
    
    // Cases
    'cases.title': 'Pipeline de Casos',
    'cases.create_new': 'Crear Nuevo Caso',
    'cases.status.lead': 'Lead',
    'cases.status.collecting_docs': 'Recolectando Documentos',
    'cases.status.docs_ready': 'Documentos Listos',
    'cases.status.submitted': 'Enviado',
    'cases.status.decision_pending': 'Pendiente de Decisión',
    'cases.status.complete': 'Completado',
    
    // Clients
    'clients.title': 'Gestión de Clientes',
    'clients.add_new': 'Agregar Nuevo Cliente',
    'clients.total_clients': 'Clientes Totales',
    'clients.with_cases': 'Con Casos',
    
    // Documents
    'documents.title': 'Inteligencia de Documentos',
    'documents.upload': 'Subir Documentos',
    'documents.required': 'Documentos Requeridos',
    
    // Billing
    'billing.title': 'Inteligencia de Facturación',
    'billing.invoices': 'Facturas',
    'billing.payments': 'Pagos',
    
    // Team
    'team.title': 'Gestión de Equipo',
    'team.members': 'Miembros del Equipo',
    'team.performance': 'Rendimiento',
    
    // Analytics
    'analytics.title': 'Panel de Analíticas',
    'analytics.overview': 'Resumen',
    'analytics.performance': 'Rendimiento'
  },
  fr: {
    // Navigation
    'nav.dashboard': 'Tableau de Bord',
    'nav.cases': 'Dossiers',
    'nav.clients': 'Clients',
    'nav.documents': 'Documents',
    'nav.billing': 'Facturation',
    'nav.team': 'Équipe',
    'nav.analytics': 'Analytiques',
    
    // Common
    'common.welcome': 'Bienvenue',
    'common.loading': 'Chargement',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.save': 'Sauvegarder',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.view': 'Voir',
    
    // Cases
    'cases.title': 'Pipeline des Dossiers',
    'cases.create_new': 'Créer un Nouveau Dossier',
    'cases.status.lead': 'Lead',
    'cases.status.collecting_docs': 'Collecte de Documents',
    'cases.status.docs_ready': 'Documents Prêts',
    'cases.status.submitted': 'Soumis',
    'cases.status.decision_pending': 'Décision en Attente',
    'cases.status.complete': 'Terminé',
    
    // Clients
    'clients.title': 'Gestion des Clients',
    'clients.add_new': 'Ajouter un Nouveau Client',
    'clients.total_clients': 'Clients Totaux',
    'clients.with_cases': 'Avec Dossiers',
    
    // Documents
    'documents.title': 'Intelligence des Documents',
    'documents.upload': 'Télécharger des Documents',
    'documents.required': 'Documents Requis',
    
    // Billing
    'billing.title': 'Intelligence de Facturation',
    'billing.invoices': 'Factures',
    'billing.payments': 'Paiements',
    
    // Team
    'team.title': 'Gestion d\'Équipe',
    'team.members': 'Membres de l\'Équipe',
    'team.performance': 'Performance',
    
    // Analytics
    'analytics.title': 'Tableau de Bord Analytique',
    'analytics.overview': 'Aperçu',
    'analytics.performance': 'Performance'
  },
  zh: {
    // Navigation
    'nav.dashboard': '仪表板',
    'nav.cases': '案件',
    'nav.clients': '客户',
    'nav.documents': '文件',
    'nav.billing': '账单',
    'nav.team': '团队',
    'nav.analytics': '分析',
    
    // Common
    'common.welcome': '欢迎',
    'common.loading': '加载中',
    'common.error': '错误',
    'common.success': '成功',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.view': '查看',
    
    // Cases
    'cases.title': '案件流水线',
    'cases.create_new': '创建新案件',
    'cases.status.lead': '潜在客户',
    'cases.status.collecting_docs': '收集文件中',
    'cases.status.docs_ready': '文件准备就绪',
    'cases.status.submitted': '已提交',
    'cases.status.decision_pending': '等待决定',
    'cases.status.complete': '完成',
    
    // Clients
    'clients.title': '客户管理',
    'clients.add_new': '添加新客户',
    'clients.total_clients': '总客户数',
    'clients.with_cases': '有案件的客户',
    
    // Documents
    'documents.title': '文件智能',
    'documents.upload': '上传文件',
    'documents.required': '必需文件',
    
    // Billing
    'billing.title': '账单智能',
    'billing.invoices': '发票',
    'billing.payments': '付款',
    
    // Team
    'team.title': '团队管理',
    'team.members': '团队成员',
    'team.performance': '绩效',
    
    // Analytics
    'analytics.title': '分析仪表板',
    'analytics.overview': '概览',
    'analytics.performance': '绩效'
  },
  // Add other languages with similar structure...
  de: {}, ar: {}, hi: {}, pt: {}
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const isRTL = language === 'ar';

  const t = (key: string): string => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  useEffect(() => {
    // Set HTML direction for RTL languages
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};